import { createContext, useContext, ReactNode, useEffect, useMemo } from 'react'
import type { ColorPalette, Typography } from '@/types/templates'
import { getTemplateColors, getTemplateTypography } from '@/lib/templates/registry'
import { getFontById } from '@/lib/fonts'
import { loadFonts } from '@/components/ui/FontLoader'

interface TemplateContextValue {
    colors: ColorPalette
    typography: Typography
    typographyId: string
}

const TemplateContext = createContext<TemplateContextValue | null>(null)

interface TemplateProviderProps {
    children: ReactNode
    colorScheme: string
    typography: string
    customFontUrl?: string // Optional custom Google Font URL
}

/**
 * TemplateProvider wraps the application with template context,
 * providing colors and typography settings. It also handles
 * dynamic font loading from Google Fonts.
 */
export function TemplateProvider({ children, colorScheme, typography, customFontUrl }: TemplateProviderProps) {
    const colors = useMemo(() => getTemplateColors(colorScheme), [colorScheme])
    const typographyData = useMemo(() => getTemplateTypography(typography), [typography])
    const typographyId = useMemo(() => typography, [typography])

    // Load fonts dynamically when typography changes
    useEffect(() => {
        const font = getFontById(typography)
        if (font) {
            loadFonts([typography])
        }
    }, [typography])

    // Handle custom font URL if provided
    useEffect(() => {
        if (customFontUrl && typeof document !== 'undefined') {
            const linkId = 'custom-font'
            if (!document.getElementById(linkId)) {
                const link = document.createElement('link')
                link.id = linkId
                link.rel = 'stylesheet'
                link.href = customFontUrl
                document.head.appendChild(link)
            }
        }
    }, [customFontUrl])

    return (
        <TemplateContext.Provider value={{ colors, typography: typographyData, typographyId }}>
            <div
                style={{
                    '--template-primary': colors.colors.primary,
                    '--template-secondary': colors.colors.secondary,
                    '--template-accent': colors.colors.accent,
                    '--template-background': colors.colors.background,
                    '--template-text': colors.colors.text,
                    '--template-text-muted': colors.colors.textMuted,
                    '--font-heading': typographyData.heading,
                    '--font-body': typographyData.body,
                } as React.CSSProperties}
            >
                {children}
            </div>
        </TemplateContext.Provider>
    )
}

export function useTemplate() {
    const context = useContext(TemplateContext)
    if (!context) {
        throw new Error('useTemplate must be used within TemplateProvider')
    }
    return context
}
